<template>
  <div>
    <h4 class="table-page-header">Shift Verification</h4>

    <!-- Filters -->
    <div class="compact-filter-section">
      <div class="filter-row-compact">
        <div class="filter-group-compact date-range-group">
          <el-date-picker
            v-model="dateRange"
            type="daterange"
            range-separator="-"
            start-placeholder="Start date"
            end-placeholder="End date"
            format="yyyy-MM-dd"
            value-format="yyyy-MM-dd"
            :clearable="true"
            @change="fetchData"
            class="compact-date-picker"
          />
        </div>
        <div class="filter-group-compact">
          <AIVSelect v-model="datePreset" :options="datePresetOptions" :searchable="false" @change="applyPreset" placeholder="Quick select" class="compact-select" />
        </div>
        <div v-if="userRole === 'Admin'" class="filter-group-compact">
          <AIVSelect
            v-model="filterAdminId"
            :options="adminOptions"
            placeholder="All Admins"
            :multiSelect="true"
            :searchable="true"
            :clearable="true"
            @change="fetchData"
            class="compact-select"
          />
        </div>
        <div class="filter-group-compact">
          <AIVSelect v-model="filterProgramId" :options="programOptions" placeholder="All Programs" @change="fetchData" class="compact-select" />
        </div>
        <div class="filter-group-compact">
          <AIVSelect
            v-model="filterStatus"
            :options="statusFilterOptions"
            placeholder="All Statuses"
            @input="fetchData"
            class="compact-select"
          />
        </div>
        <div class="filter-group-compact">
          <el-button type="primary" size="small" @click="downloadCsv">
            <i class="fa fa-download"></i> CSV
          </el-button>
        </div>
      </div>
    </div>

    <!-- Summary Bar -->
    <div class="verification-summary" v-if="summary">
      <div class="summary-stat">
        <span class="summary-stat__value">{{ summary.total }}</span>
        <span class="summary-stat__label">Total</span>
      </div>
      <div class="summary-stat summary-stat--green">
        <span class="summary-stat__value">{{ summary.verified }}</span>
        <span class="summary-stat__label">Verified</span>
      </div>
      <div class="summary-stat summary-stat--gray">
        <span class="summary-stat__value">{{ summary.unverified }}</span>
        <span class="summary-stat__label">Unverified</span>
      </div>
      <div class="summary-stat">
        <span class="summary-stat__value">{{ summary.percentage }}%</span>
        <span class="summary-stat__label">Rate</span>
      </div>
    </div>

    <!-- Search -->
    <div class="filter-section">
      <div class="filter-row">
        <div class="filter-group search-group">
          <el-input
            v-model="searchQuery"
            placeholder="Search shifts..."
            clearable
            @clear="searchQuery = ''"
            class="search-input"
          >
            <i
              slot="prepend"
              class="fa fa-search"
              style="padding-left: 4px; padding-right: 4px"
            ></i>
          </el-input>
        </div>
      </div>
    </div>

    <!-- Verification Table -->
    <AIVTable
      :columns="columns"
      :data="paginatedShifts"
      :pagination="pagination"
      :item-name="'shifts'"
      :loading="loading"
      @pageChange="onPageChange"
      @pageSizeChange="onPageSizeChange"
    >
      <template #cell-verified="{ row }">
        <VerificationShield :verified="row.verified" />
      </template>
      <template #cell-scheduledStart="{ row }">
        {{ formatDate(row.scheduledStart) }}
      </template>
      <template #cell-caregivers="{ row }">
        {{ (row.caregivers || []).map(c => c.name).join(', ') }}
      </template>
      <template #cell-clients="{ row }">
        {{ (row.clients || []).map(c => c.name).join(', ') }}
      </template>
      <template #cell-clockIn="{ row }">
        {{ formatTime(row.clockIn) }}
      </template>
      <template #cell-clockOut="{ row }">
        {{ formatTime(row.clockOut) }}
      </template>
      <template #cell-serviceCode="{ row }">
        <span>{{ row.serviceCode || '-' }}</span>
      </template>
      <template #cell-serviceDescription="{ row }">
        <div>
          <span>{{ row.serviceDescription || '-' }}</span>
          <div v-if="row.serviceCode" class="text-muted small">
            {{ row.billable ? 'Billable' : 'Non-Billable' }}
          </div>
        </div>
      </template>
      <template #cell-shiftStatus="{ row }">
        <el-tag size="mini" :type="row.verified ? 'success' : 'info'">
          {{ row.shiftStatus }}
        </el-tag>
      </template>
    </AIVTable>
  </div>
</template>

<script>
import { verificationService } from "@/serviceHandlers/verificationService";
import { cbsProgramsService } from "@/serviceHandlers/cbsProgramsService";
import { caregiversService } from "@/serviceHandlers/caregiversService";
import { useLoginStore } from "@/stores/loginStore";
import VerificationShield from "@/components/VerificationShield.vue";
import dayjs from "dayjs";

export default {
  name: "Verification",
  components: { VerificationShield },
  data() {
    return {
      shifts: [],
      summary: null,
      programs: [],
      admins: [],
      userRole: null,
      dateRange: null,
      datePreset: "thisWeek",
      filterProgramId: null,
      filterAdminId: [],
      filterStatus: null,
      loading: false,
      searchQuery: "",
      page: 1,
      pageSize: 20,
      columns: [
        { key: "verified", label: "", sortable: false },
        { key: "scheduledStart", label: "Date" },
        { key: "caregivers", label: "Caregiver" },
        { key: "clients", label: "Client" },
        { key: "clockIn", label: "Clock In" },
        { key: "clockOut", label: "Clock Out" },
        { key: "programName", label: "Program" },
        { key: "serviceCode", label: "Service Code", minWidth: "120", sortable: true },
        { key: "serviceDescription", label: "Service Description", minWidth: "180", sortable: true },
        { key: "shiftStatus", label: "Status" },
      ],
      datePresetOptions: [
        { id: "today", name: "Today" },
        { id: "thisWeek", name: "This Week" },
        { id: "lastWeek", name: "Last Week" },
        { id: "thisMonth", name: "This Month" },
      ],
    };
  },
  computed: {
    programOptions() {
      return this.programs.map((p) => ({
        id: p.id,
        name: p.display_name,
      }));
    },
    adminOptions() {
      return this.admins.map((a) => ({
        id: Number(a.id) || a.id,
        name: a.name,
      }));
    },
    statusFilterOptions() {
      return [
        { id: "unverified", name: "Unverified" },
        { id: "verified", name: "Verified" },
      ];
    },
    searchedShifts() {
      if (!this.searchQuery) return this.shifts;
      const q = this.searchQuery.toLowerCase();
      return this.shifts.filter((s) => {
        const caregiverNames = (s.caregivers || []).map((c) => c.name).join(" ").toLowerCase();
        const clientNames = (s.clients || []).map((c) => c.name).join(" ").toLowerCase();
        const program = (s.programName || "").toLowerCase();
        const status = (s.shiftStatus || "").toLowerCase();
        return caregiverNames.includes(q) || clientNames.includes(q) || program.includes(q) || status.includes(q)
          || (s.serviceCode || "").toLowerCase().includes(q)
          || (s.serviceDescription || "").toLowerCase().includes(q);
      });
    },
    paginatedShifts() {
      const start = (this.page - 1) * this.pageSize;
      return this.searchedShifts.slice(start, start + this.pageSize);
    },
    pagination() {
      return {
        total: this.searchedShifts.length,
        perPage: this.pageSize,
        currentPage: this.page,
        totalPages: Math.ceil(this.searchedShifts.length / this.pageSize) || 1,
      };
    },
  },
  async mounted() {
    const loginStore = useLoginStore();
    this.userRole = loginStore.user?.user_type || null;
    this.applyPreset("thisWeek");
    await Promise.all([this.fetchPrograms(), this.fetchAdmins()]);
  },
  methods: {
    async fetchPrograms() {
      try {
        this.programs = await cbsProgramsService.list();
      } catch { /* ignore */ }
    },
    async fetchAdmins() {
      if (this.userRole !== "Admin") return;
      try {
        this.admins = (await caregiversService.getAdmins()) || [];
      } catch { /* ignore */ }
    },
    async fetchData() {
      if (!this.dateRange || this.dateRange.length < 2) return;
      this.loading = true;
      try {
        const params = {
          startDate: this.dateRange[0],
          endDate: this.dateRange[1],
        };
        if (this.filterProgramId) params.programId = this.filterProgramId;
        if (this.filterAdminId && this.filterAdminId.length > 0) {
          params.adminId = this.filterAdminId.join(",");
        }
        if (this.filterStatus) {
          params.status = this.filterStatus;
        }

        const [shiftsResp, summaryResp] = await Promise.all([
          verificationService.getShifts(params),
          verificationService.getSummary(params),
        ]);
        this.shifts = shiftsResp;
        this.summary = summaryResp;
        this.page = 1;
      } catch (error) {
        this.$notifyError("Could not load verification data: " + error);
      } finally {
        this.loading = false;
      }
    },
    applyPreset(preset) {
      this.datePreset = preset;
      const today = dayjs();
      switch (preset) {
        case "today":
          this.dateRange = [today.format("YYYY-MM-DD"), today.format("YYYY-MM-DD")];
          break;
        case "thisWeek":
          this.dateRange = [today.startOf("week").format("YYYY-MM-DD"), today.endOf("week").format("YYYY-MM-DD")];
          break;
        case "lastWeek":
          this.dateRange = [today.subtract(1, "week").startOf("week").format("YYYY-MM-DD"), today.subtract(1, "week").endOf("week").format("YYYY-MM-DD")];
          break;
        case "thisMonth":
          this.dateRange = [today.startOf("month").format("YYYY-MM-DD"), today.endOf("month").format("YYYY-MM-DD")];
          break;
      }
      this.fetchData();
    },
    async downloadCsv() {
      if (!this.dateRange || this.dateRange.length < 2) return;
      try {
        const params = {
          startDate: this.dateRange[0],
          endDate: this.dateRange[1],
        };
        if (this.filterProgramId) params.programId = this.filterProgramId;
        if (this.filterAdminId && this.filterAdminId.length > 0) {
          params.adminId = this.filterAdminId.join(",");
        }
        if (this.filterStatus) {
          params.status = this.filterStatus;
        }

        const blob = await verificationService.downloadCsv(params);
        const url = window.URL.createObjectURL(new Blob([blob]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "verification_report.csv");
        document.body.appendChild(link);
        link.click();
        link.remove();
      } catch (error) {
        this.$notifyError("Could not download CSV: " + error);
      }
    },
    onPageChange(p) { this.page = p; },
    onPageSizeChange(s) { this.pageSize = s; this.page = 1; },
    formatDate(d) { return d ? dayjs(d).format("MM/DD/YYYY") : ""; },
    formatTime(d) { return d ? dayjs(d).format("h:mm A") : ""; },
  },
};
</script>

<style scoped>
.compact-filter-section {
  --primary-color: #6BA6CD;
  --primary-hover: #5A94B8;
  --background-glass: rgba(255, 255, 255, 0.08);
  --border-color: rgba(255, 255, 255, 0.12);
  --text-primary: #fff;
  --text-secondary: rgba(255, 255, 255, 0.7);
  --shadow-subtle: 0 2px 8px rgba(0, 0, 0, 0.1);

  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
  padding: 0;
}

.filter-row-compact {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.5rem 1rem;
  background: var(--background-glass);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  box-shadow: var(--shadow-subtle);
  transition: all 0.2s ease;
  flex-wrap: wrap;
}

.filter-row-compact:hover {
  border-color: rgba(255, 255, 255, 0.18);
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.12);
}

.filter-group-compact {
  display: flex;
  align-items: center;
  flex: 0 0 auto;
  min-width: 180px;
}

.compact-select {
  width: 100%;
}

.date-range-group {
  min-width: 500px;
  flex-grow: 1;
  max-width: 600px;
}

.compact-date-picker {
  width: 100% !important;
}

/* Date Range Picker Dark Theme Styling */
::v-deep .el-date-editor {
  width: 100%;
}

::v-deep .el-date-editor .el-input__inner {
  background-color: #656565 !important;
  border: 1px solid #4a5568;
  color: #ffffff;
}

::v-deep .el-date-editor .el-input__inner:focus {
  border-color: #4caf50;
}

::v-deep .el-date-editor .el-input__icon {
  color: #a0aec0;
}

::v-deep .el-date-editor--daterange {
  width: 100% !important;
}

::v-deep .el-date-editor--daterange .el-input__suffix {
  right: 10px !important;
}

::v-deep .el-range-separator {
  color: #ffffff !important;
  padding: 0 5px !important;
}

::v-deep .el-range-input {
  background-color: #656565 !important;
  color: #ffffff !important;
}

::v-deep .el-range__close-icon {
  right: 10px !important;
}

::v-deep .el-date-editor--daterange .el-range__icon {
  color: #a0aec0;
}

/* Summary Bar */
.verification-summary { display: flex; gap: 1.5rem; margin-bottom: 1.5rem; padding: 1rem; background: rgba(37, 55, 65, 0.95); border-radius: 8px; }
.summary-stat { text-align: center; }
.summary-stat__value { display: block; font-size: 1.5rem; font-weight: 700; color: #fff; }
.summary-stat__label { font-size: 0.8rem; color: #b3a6a6; }
.summary-stat--green .summary-stat__value { color: #67c23a; }
.summary-stat--gray .summary-stat__value { color: #909399; }

/* Search */
.filter-section { margin-bottom: 1rem; }
.filter-row { display: flex; gap: 1rem; align-items: flex-end; flex-wrap: wrap; }
.filter-group { display: flex; flex-direction: column; gap: 0.25rem; }
.filter-group.search-group { flex: 1; max-width: 300px; }

.text-muted { color: #999; }
.small { font-size: 12px; }

@media (max-width: 768px) {
  /* Filters: two-column grid, date range spanning the full row */
  .filter-row-compact {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0.5rem;
    padding: 0.6rem 0.75rem;
  }

  .filter-group-compact {
    min-width: 0;
    width: 100%;
  }

  .filter-group-compact.date-range-group {
    grid-column: 1 / -1;
    min-width: 0;
    max-width: unset;
  }

  .verification-summary {
    flex-wrap: wrap;
    gap: 1rem;
    justify-content: space-around;
    padding: 0.75rem;
  }
}
</style>
